import { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import type { HTMLAttributes, LabelHTMLAttributes, SelectHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { theme } from './styles/theme';
import { ErrorMessage, Spinner } from './styles/components';
import { BudgetApiClient } from './api/client';
import { useWidgetState } from './hooks/useWidgetState';
import { useBudgetData } from './hooks/useBudgetData';
import { useFilterOptions } from './hooks/useFilterOptions';
import { useFilterMatrix } from './hooks/useFilterMatrix';
import { useFilterAvailability } from './hooks/useFilterAvailability';
import { SearchAutocomplete } from './components/SearchAutocomplete';
import { FilterPanel } from './components/FilterPanel';
import { ActiveFilterChips } from './components/ActiveFilterChips';
import type { ChipKey } from './components/ActiveFilterChips';
import { ExpenditureAreaChart } from './components/ExpenditureAreaChart';
import { ExpenditureBarChart } from './components/ExpenditureBarChart';
import { BudgetTable } from './components/BudgetTable';
import { Spotlights, SPOTLIGHTS, type Spotlight } from './components/Spotlights';
import { EmbedDialog } from './components/EmbedDialog';
import { buildFilterInfoText } from './utils/filterInfo';
import type { EmbedChartKind } from './utils/embedConfig';
import { useBudgetEntries } from './hooks/useBudgetEntries';
import { useAsync } from './hooks/useAsync';
import { categoryLabel, getLocale, t, useT } from './i18n';
import type { BudgetWidgetProps, BudgetTableEntry, SearchSuggestion, DisplayUnit, FilterOption, SelectedTitle } from './types';

// Map the linear drill-down state to a single depth (0 = root, 1 = sub-type,
// 2 = titles, 3 = single title selected). We mirror this depth into browser
// history so the back button steps back up the drill instead of leaving the
// host page. `selectedTitle` outranks the drill levels because a title can be
// selected while drillDownSubType is also set (e.g. via search).
function computeDrillDepth(
  drillDownType: string | null,
  drillDownSubType: string | null,
  selectedTitle: SelectedTitle | null,
): number {
  if (selectedTitle) return 3;
  if (drillDownSubType !== null) return 2;
  if (drillDownType !== null) return 1;
  return 0;
}

// `dz-budget-widget-root color-data` are the scoping anchors for the
// embedded widget. `.color-data` (shipped by dz-shared-styles tokens.scss)
// sets `--primary-color: var(--color-data)`; we also hard-set
// `--primary-color-hover` here so button/dropdown :hover states still
// resolve when the host page only loads the base tokens.
//
// font-size: 16px overrides the shared `html, body { font-size: 13px }`
// rule. All shared typography mixins (sans-s 0.8125rem, serif-l 1.875rem,
// etc.) are rem-based and assume a ~16px root for the proportions seen in
// the website screenshots — at the shared 13px default, dashboard text
// shrinks to ~10px which is unreadable. Setting the root here scales
// every rem-based descendant correctly without touching individual sizes.
const WidgetContainer = styled.div<{ isDark?: boolean }>`
  font-family: ${theme.fontFamily};
  font-size: 16px;
  color: ${theme.colors.text.primary};
  background: ${props => props.isDark ? '#1a1a2e' : theme.colors.background};
  min-height: 600px;
  --primary-color-hover: var(--color-data-700);
`;

const MainContentArea = styled.div`
  display: flex;
  flex-direction: column;
  /* Figma measures 36 px between the toggle row (y 682) and the search bar
     (y 718). --space-12 is the shared token at that value. */
  gap: var(--space-12);
  margin-bottom: ${theme.spacing.xl};
`;

// Two-column body: left = search + charts, right = persistent filter sidebar.
// On narrow viewports the sidebar drops below the main column so the chart
// always gets full width when space is tight.
const ContentSplit = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 328px;
  gap: ${theme.spacing.lg};
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  /* Figma measures 79 px between search bar bottom (y 744) and the chart
     title (y 823). The chart card already carries ~24 px internal
     padding-top, so the sibling-level gap is 79 − 24 ≈ 55 px → --space-18
     (54 px). */
  gap: var(--space-18);
  min-width: 0;
`;

// Search input + the active-filter chip bar form one block, so the chips sit
// directly under the search bar instead of inheriting MainColumn's 54 px
// chart gap. Collapses to just the input when no filter is active (the chip
// bar renders nothing).
const SearchBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
`;

// Sticky filter sidebar. With `align-items: start` on ContentSplit, this
// grid item keeps its natural height (the FilterCard's height) while the
// grid row stretches to match the much taller main column (charts + table).
// `position: sticky` lets the sidebar dock to the viewport as the user
// scrolls the main column, so filter controls remain reachable throughout
// long pages. The top offset gives a small visual gutter under any host
// page chrome that may sit above the widget.
const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  min-width: 0;
  position: sticky;
  top: var(--sticky-below-nav);
  align-self: start;
  /* position:sticky creates a stacking context unconditionally, which traps
     the FilterCard's (and its dropdowns') z-index:1000 *inside* this element.
     Without an explicit z-index here, the sidebar's whole context sits at the
     root 'auto' level — below the BudgetTable's sticky header (Th, z-index:1),
     so the "Soll (…)" column would paint over an open filter dropdown. Lifting
     the sidebar's own context above the table's low z-indices fixes that. */
  z-index: 20;

  /* Sticky stops working on narrow viewports where the sidebar drops below
     the main column (see ContentSplit @media rule below). Reset to static
     there so it scrolls naturally with the rest of the page. */
  @media (max-width: 1024px) {
    position: static;
    top: auto;
  }

  /* On phones the filter collapses to the compact "Filter" sheet trigger
     (see FilterPanel). Left in DOM order it would land below the chart; pull it
     above the main column so it sits near the top — directly under the top
     controls, mirroring the investments widget. Scoped to the sheet breakpoint
     (incl. short landscape viewports, matching FilterPanel's PHONE query) so
     the tablet single-column accordion keeps its after-chart position. */
  @media (max-width: 600px), (max-height: 600px) and (orientation: landscape) {
    order: -1;
  }
`;

const ChartsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

// Top-level page controls: the Einnahmen/Ausgaben mode switch and the inline
// Einheit selector. Both change how the entire dashboard is read (dataset vs.
// display scaling), so they sit above the search bar — anchored to the left
// to mirror the page-level reading order in the screenshot.
const TopControlsRow = styled.div`
  display: flex;
  justify-content: flex-start;
  /* The Einheit selector is two rows tall (caption + pill); anchor everything
     along the bottom so the toggle pills line up with the dropdown pill. */
  align-items: flex-end;
  gap: ${theme.spacing.lg};
  flex-wrap: wrap;
`;

// Segmented control — two adjacent pills using the shared `.button` family.
// Active uses `--primary` (resolves to `--color-data` via the widget's
// `.color-data` scope); inactive uses `--secondary` (white bg). Keeping the
// pills flush (no gap) reads as a single control unit, matching the Figma
// segmented design.
const TypeToggleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
`;

// Mapping to shared .button modifiers:
//   active   → .button--primary    (solid --color-data fill, ≈ Figma's mint)
//   inactive → .button--outline    (transparent + black 1 px border)
// .button--size-m gives 16 px sans, matching Figma's UI(S). The shared
// system has no grey-fill variant, so the inactive's exact #D9D9D9 fill
// isn't reachable without a per-instance override — outline is the closest
// "off pill" shape on offer.
const TypeToggleButton = (
  { isActive, ...rest }: { isActive: boolean } & HTMLAttributes<HTMLDivElement>,
) => (
  <div
    {...rest}
    aria-pressed={isActive}
    className={`button button--size-m ${isActive ? 'button--primary button--active' : 'button--outline'} ${rest.className ?? ''}`.trim()}
  />
);

// Shared `.dropdown-labeled` container — renders a small caption above the
// dropdown from `data-label`. Sits in the top controls row next to the
// Einnahmen/Ausgaben switch. The unit changes how values are displayed
// (Mrd. €, % BIP, % Haushalt), not what data is loaded.
const UnitDropdownLabeled = (
  props: LabelHTMLAttributes<HTMLLabelElement> & { 'data-label': string },
) => (
  <label
    {...props}
    className={`dropdown-labeled ${props.className ?? ''}`.trim()}
  />
);

// Shared `.dropdown.dropdown--outline.dropdown--size-m` — matches Figma's
// Einheit pill: transparent fill, 1 px solid black border, 16 px sans text.
// The shared `--outline` modifier swaps the filled `.dropdown` default for
// the outlined variant; `--size-m` brings the text up to 16 px (UI(S)).
//
// Hover override: the shared `--outline` rule tints the pill in
// `var(--primary-color)` (green under our `.color-data` scope) on hover.
// Figma keeps the Einheit pill transparent in every state, so the styled
// wrapper pins both `:hover` and `:hover.dropdown--active` back to
// transparent on this one instance.
const UnitNativeSelectBase = styled('select')`
  &.dropdown--outline:hover,
  &.dropdown--outline.dropdown--active:hover {
    background-color: transparent;
  }
`;
const UnitNativeSelect = (props: SelectHTMLAttributes<HTMLSelectElement>) => (
  <UnitNativeSelectBase
    {...props}
    className={`dropdown dropdown--outline dropdown--size-m ${props.className ?? ''}`.trim()}
  />
);

// A function, not a constant: labels follow the widget language, which is set
// at render time (after this module is imported).
function unitOptionsBase(): FilterOption[] {
  const m = t();
  return [
    { value: 'absolute', label: m.units.absolute, count: 0 },
    { value: 'gdp_percent', label: m.units.gdpPercent, count: 0 },
    { value: 'budget_percent', label: m.units.budgetPercent, count: 0 },
  ];
}

const InitialLoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${theme.spacing.md};
`;

const LoadingText = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
`;

const ChartSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const FooterRemark = styled.div`
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
`;

// Footer metadata — sans for dense factual text, matches the small caption
// treatment used elsewhere on the website (data dashboards aren't body
// prose; they're tabular).
const FooterDates = styled.p`
  font-family: var(--sans-stack);
  font-size: 1rem;
  line-height: 1.2;
  color: ${theme.colors.text.muted};
  text-align: left;
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const FooterText = styled.p`
  font-family: var(--sans-stack);
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const FooterContact = styled.p`
  font-family: var(--sans-stack);
  font-size: 1rem;
  line-height: 1.2;
  color: ${theme.colors.text.muted};
  margin: ${theme.spacing.sm} 0 0 0;

  a {
    color: inherit;
  }
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  background: #fff5f5;
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.error};
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
`;

const ErrorBannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-shrink: 0;
`;

// Shared `.button.button--outline.button--size-s` shape + dz palette,
// recoloured for the error/retry semantics. Pattern repeats across the
// codebase: emotion handles per-instance overrides, the wrapper attaches
// the shared BEM classes so call sites stay unchanged.
const ErrorBannerRetryBase = styled('div')`
  color: ${theme.colors.error};
  border-color: ${theme.colors.error};
  &:hover {
    background: ${theme.colors.error};
    color: white;
  }
`;
const ErrorBannerRetry = (props: HTMLAttributes<HTMLDivElement>) => (
  <ErrorBannerRetryBase
    {...props}
    className={`button button--outline button--size-s ${props.className ?? ''}`.trim()}
  />
);

const ErrorBannerClose = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${theme.colors.error};
  border-radius: ${theme.borderRadius.md};
  flex-shrink: 0;

  &:hover {
    background: rgba(244, 67, 54, 0.1);
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: rgba(24, 28, 68, 0.95);
  color: white;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  max-width: 360px;
`;

export function BudgetWidget({
  apiBaseUrl,
  initialFilters,
  onFilterChange,
  theme: themeMode = 'light',
  className,
  stateCarry,
}: BudgetWidgetProps) {
  const m = useT();

  // Initialize API client
  const apiClient = useMemo(
    () => new BudgetApiClient(apiBaseUrl),
    [apiBaseUrl],
  );

  // Record one page-view per mount (fire-and-forget). The ref guards against
  // React StrictMode's double-invoke in dev so we don't double-count.
  const pageviewSent = useRef(false);
  useEffect(() => {
    if (pageviewSent.current) return;
    pageviewSent.current = true;
    apiClient.trackPageview();
  }, [apiClient]);

  // Initialize widget state
  const {
    state,
    setFilter,
    setFilterWithDrillDown,
    setFiltersWithDrillDown,
    clearFilters,
    setYear,
    setType,
    drillDown,
    drillDownSub,
    drillUp,
    selectTitle,
    setDisplayUnit,
    toggleTitleBreakout,
  } = useWidgetState(
    initialFilters ? {
      einzelplan: initialFilters.einzelplan || null,
      kapitel: initialFilters.kapitel || null,
      query: initialFilters.query || null,
      expenditureType: initialFilters.expenditureType || null,
      subExpType: null,
    } : undefined,
    initialFilters?.year,
    stateCarry,
  );

  // Ref for scrolling to area chart
  const areaChartRef = useRef<HTMLDivElement>(null);

  // Track if we just drilled up - prevents auto-skip from firing when going backwards
  const justDrilledUpRef = useRef(false);

  // GDP and budget-totals fetches share the same shape (load once, refetchable
  // via useAsync). The retry button in the error banner calls refetch directly.
  const {
    data: gdpData,
    error: gdpError,
    refetch: fetchGdpData,
  } = useAsync(() => apiClient.getGdpData(), [apiClient]);

  const {
    data: budgetTotals,
    error: budgetTotalsError,
    refetch: fetchBudgetTotals,
  } = useAsync(() => apiClient.getBudgetTotals(state.filters.type), [apiClient, state.filters.type]);

  // Timestamp of the most recent budget data load — drives the footer's
  // "Letztes Update" line so it tracks the real refresh, not a hardcoded date.
  const { data: lastUpdatedData } = useAsync(() => apiClient.getLastUpdated(), [apiClient]);

  // Toast for transient unit-switch notifications
  const [toast, setToast] = useState<string | null>(null);

  // Dismissible error banner state — reset when a new error appears
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Which chart's "Einbetten" dialog is open, if any.
  const [embedChart, setEmbedChart] = useState<EmbedChartKind | null>(null);

  // Auto-snap displayUnit to absolute when its underlying data is unavailable.
  // Without this, the chart would silently render absolute values labelled as a percentage.
  useEffect(() => {
    if (state.displayUnit === 'gdp_percent' && gdpError) {
      setDisplayUnit('absolute');
      setToast(m.widget.toastGdpUnavailable);
    } else if (state.displayUnit === 'budget_percent' && budgetTotalsError) {
      setDisplayUnit('absolute');
      setToast(m.widget.toastTotalsUnavailable);
    }
  }, [state.displayUnit, gdpError, budgetTotalsError, setDisplayUnit, m]);

  // Auto-dismiss toast after 5s
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(id);
  }, [toast]);

  // Fetch data
  const { data, subData, titleData, subColorMap, titleColorMap, isLoading, isLoadingSubData, isLoadingTitleData, error } = useBudgetData(
    apiClient,
    state.filters,
    state.drillDownType,
    state.drillDownSubType,
    state.selectedTitle,
    state.brokenOutTitleIds
  );

  // Fetch base filter options. Flat lists feed chart label lookups; tree
  // narrowing comes from useFilterAvailability below, not from this fetch.
  const { options: filterOptions, error: filterOptionsError, refetch: refetchFilterOptions } = useFilterOptions(
    apiClient,
    state.filters.einzelplan,
    state.filters.type,
  );

  // Cross-dimension faceting: fetch the filter matrix once per `type`, then
  // compute per-dimension availability maps (value → count under the other
  // current filters, with self-exclusion). FilterPanel uses these to grey
  // out unreachable nodes in each cascading select while keeping the full
  // tree visible for layout stability.
  const { matrix: filterMatrix } = useFilterMatrix(apiClient, state.filters.type);
  const filterAvailability = useFilterAvailability(filterMatrix, state.filters);

  // Re-show banner whenever a new error appears so a previously-dismissed
  // banner doesn't hide a fresh failure.
  useEffect(() => {
    if (gdpError || budgetTotalsError || filterOptionsError) {
      setBannerDismissed(false);
    }
  }, [gdpError, budgetTotalsError, filterOptionsError]);

  // Retry only the fetches that are currently errored.
  const handleRetry = useCallback(() => {
    if (gdpError) fetchGdpData();
    if (budgetTotalsError) fetchBudgetTotals();
    if (filterOptionsError) refetchFilterOptions();
  }, [gdpError, budgetTotalsError, filterOptionsError, fetchGdpData, fetchBudgetTotals, refetchFilterOptions]);

  // The widget opens on the newest year the data actually has, so it follows
  // each new Haushalt without a code change. budgetTotals is unfiltered, so the
  // default doesn't jump around as the user filters; the chart rows are the
  // fallback for the degraded path where the totals fetch failed. Both are in
  // hand before the first paint (see the isUnitDataLoading gate below), so the
  // resolved year is right on the first render — no snap, no empty-chart flash.
  const latestDataYear = useMemo(() => {
    const years = budgetTotals?.length
      ? budgetTotals.map(t => t.year)
      : (data?.expenditureByYearAndType ?? []).map(d => d.year);
    return years.length ? Math.max(...years) : new Date().getFullYear();
  }, [budgetTotals, data]);
  const effectiveYear = state.selectedYear ?? latestDataYear;

  // A human-readable name for the current view, used as the embed iframe's
  // `title` — which is what a screen reader announces for the frame, so it has
  // to describe the chart rather than the widget.
  const embedChartTitle = useMemo(() => {
    const root = state.filters.type === 'E'
      ? m.widget.rootRevenue
      : m.widget.rootExpenditure;
    // A compound drill value is "parent|sub"; only the sub half names the view.
    const drillSub = state.drillDownSubType?.split('|').pop();
    const subject =
      state.selectedTitle?.zweckbestimmung ||
      (drillSub ? categoryLabel(drillSub) : '') ||
      (state.drillDownType ? categoryLabel(state.drillDownType) : '') ||
      root;
    const scope = buildFilterInfoText(
      {
        einzelplan: state.filters.einzelplan,
        kapitel: state.filters.kapitel,
        funktion: state.filters.funktion,
        oberfunktion: state.filters.oberfunktion,
        hauptfunktion: state.filters.hauptfunktion,
        gruppierung: state.filters.gruppierung,
        obergruppe: state.filters.obergruppe,
        hauptgruppe: state.filters.hauptgruppe,
        prefix: state.filters.prefix,
        kapitelScope: state.filters.kapitelScope,
        // `q` never narrows the data server-side, so it must not appear in a
        // label that describes what the embed shows.
        query: null,
      },
      filterOptions,
    );
    return scope ? `${subject} — ${scope}` : subject;
  }, [state.filters, state.selectedTitle, state.drillDownType, state.drillDownSubType, filterOptions, m]);

  // Fetch budget entries for table
  const {
    entries,
    isLoading: entriesLoading,
    totalCount,
    pageCount,
    currentPage,
    goToPage,
  } = useBudgetEntries(
    apiClient,
    state.filters,
    state.drillDownType,
    state.drillDownSubType,
    effectiveYear
  );

  // Format the last-updated timestamp as a long date in the widget locale
  // (e.g. "6. Juli 2026" / "6 July 2026"), pinned to Berlin time so it doesn't drift a day near midnight UTC.
  // Falls back to the original first-publication date until the fetch resolves
  // (or if it fails), so the footer never renders a gap.
  const lastUpdatedLabel = useMemo(() => {
    const iso = lastUpdatedData?.lastUpdated;
    if (!iso) return m.widget.firstPublishedDate;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return m.widget.firstPublishedDate;
    return new Intl.DateTimeFormat(getLocale(), {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Berlin',
    }).format(d);
  }, [lastUpdatedData, m]);

  // Compute yearsWithIst from budgetTotals (unfiltered, only type: E|A)
  const yearsWithIst = useMemo(() => {
    if (!budgetTotals) return new Set<number>();
    return new Set(
      budgetTotals
        .filter(t => t.total_ist != null && t.total_ist > 0)
        .map(t => t.year)
    );
  }, [budgetTotals]);

  // Notify parent of filter changes. Stash the handler in a ref so a parent
  // that doesn't memoize its onFilterChange callback can't trigger spurious
  // re-fires of this effect on every parent render — we only want to fire
  // when the filters themselves change.
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;
  useEffect(() => {
    onFilterChangeRef.current?.(state.filters);
  }, [state.filters]);

  // Auto-skip to titles when there's only one sub-type
  // This avoids a redundant click when the sub-expenditure level provides no additional breakdown
  useEffect(() => {
    // Only trigger when:
    // 1. We have sub-data loaded
    // 2. We're at level 2 (drillDownType set, but drillDownSubType not set)
    // 3. Not currently loading
    // 4. Not just drilled up (prevents infinite loop when going backwards)
    // Compare drillDownSubType against null specifically: '' is a valid level-3 state
    // (auto-skipped categories and Einnahmen) and must not re-trigger the skip,
    // which would clobber selectedTitle via DRILL_DOWN_SUB.
    if (!subData || !state.drillDownType || state.drillDownSubType !== null || isLoadingSubData) return;

    // Get unique sub-types from the data
    const uniqueSubTypes = [...new Set(subData.map(d => d.expenditure_type))];

    // Check if we just drilled up - if so, skip auto-skip and go to level 1 instead
    if (justDrilledUpRef.current) {
      justDrilledUpRef.current = false;
      // Skip level 2 if there's only one sub-type (no meaningful breakdown)
      if (uniqueSubTypes.length === 1) {
        // Go directly to level 1 since level 2 is redundant
        drillUp();
      }
      return;
    }

    // Auto-skip to titles if there's only one sub-type (no meaningful breakdown at this level)
    if (uniqueSubTypes.length === 1) {
      const subTypeValue = uniqueSubTypes[0] === 'Nicht zugeordnet' ? '' : uniqueSubTypes[0];
      drillDownSub(subTypeValue);
    }
  }, [subData, state.drillDownType, state.drillDownSubType, isLoadingSubData, drillDownSub, drillUp]);

  // Handlers
  const handleSearchSelect = useCallback((suggestion: SearchSuggestion | null) => {
    if (suggestion && suggestion.expenditureType) {
      // Use subExpType if available, otherwise empty string (for Einnahmen which have no sub-types)
      const subExpType = suggestion.subExpType || '';
      // Drill down to sub-type level (NOT filtering by query - show all titles in sub-type)
      // Don't filter by einzelplan/kapitel - title ID spans multiple chapters over time
      // (e.g., titles that changed Kapitel between years need to show all historical data)
      setFiltersWithDrillDown(
        {
          // NO query filter - show all titles in sub-type
          expenditureType: suggestion.expenditureType,
          subExpType: `${suggestion.expenditureType}|${subExpType}`,
          einzelplan: null,
          kapitel: null,
        },
        suggestion.expenditureType,
        subExpType
      );
      // Select the title for highlighting + breadcrumb display
      selectTitle({
        id: suggestion.value,
        zweckbestimmung: suggestion.label,  // Already formatted as "(code) name"
      });
    } else if (suggestion) {
      // Fallback: just filter without drill-down (e.g., when entering raw text)
      setFilter('query', suggestion.value);
    } else {
      // Clear search and title selection - reset to default view
      // Must clear ALL drill-down related filters, not just query
      setFiltersWithDrillDown(
        { query: null, expenditureType: null, subExpType: null },
        null,  // drillDownType
        null   // drillDownSubType
      );
      selectTitle(null);
    }
  }, [setFiltersWithDrillDown, setFilter, selectTitle]);

  // Spotlight handlers — these set a single filter slot directly. The
  // CascadingSelect tree handlers below (handle*TreeChange) own the per-tree
  // multi-slot updates; these stay because Spotlights presets bypass the
  // cascading select and write to FilterState directly (e.g. the Investitionen
  // preset which sets hauptgruppe='7,8' as a multi-value workaround).

  const handleKapitelChange = useCallback((value: string | null) => {
    setFilter('kapitel', value);
  }, [setFilter]);

  const handleExpenditureTypeChange = useCallback((value: string | null) => {
    if (value) {
      // Set filter and drill down to level 2 (sub-types)
      setFilterWithDrillDown('expenditureType', value, value, null);
    } else {
      // Clear filter and reset drill-down
      setFilter('expenditureType', null);
    }
  }, [setFilterWithDrillDown, setFilter]);

  const handleHauptgruppeChange = useCallback((value: string | null) => {
    setFilter('hauptgruppe', value);
  }, [setFilter]);

  const handlePrefixChange = useCallback((value: string | null) => {
    setFilter('prefix', value);
  }, [setFilter]);

  const handleKapitelScopeChange = useCallback((value: string | null) => {
    setFilter('kapitelScope', value);
  }, [setFilter]);

  const handleSubExpTypeChange = useCallback((value: string | null) => {
    if (value && value.includes('|')) {
      // Parse compound value: "ParentType|SubType"
      const [parentType, subType] = value.split('|');

      // Store compound value in filter state (for dropdown selection to work)
      // Set expenditureType filter to parent type
      // Drill down to level 3 using the parsed values
      setFiltersWithDrillDown(
        { expenditureType: parentType, subExpType: value },
        parentType,
        subType
      );
    } else {
      // Fallback for clearing or old format
      setFilter('subExpType', value);
    }
  }, [setFiltersWithDrillDown, setFilter]);

  // ─── Tree-cascading-select handlers ─────────────────────────────────────
  // Each tree's CascadingSelect passes (value, depth) where depth is the tree
  // level the picked node lives at. We translate that into setting the right
  // FilterState slot and clearing the orthogonal ones, so only one slot per
  // tree is ever set — matching how buildWhereClause already reads them
  // ("deepest set wins") and avoiding inconsistent state.

  const handlePolitikfeldTreeChange = useCallback((value: string | null, depth: number | null) => {
    if (!value) {
      setFiltersWithDrillDown({ expenditureType: null, subExpType: null }, null, null);
      return;
    }
    if (depth === 1) {
      // Pick at top level: clear subExpType too — any held subExpType belongs
      // to the previous parent and would now be inconsistent with the new pick.
      setFiltersWithDrillDown(
        { expenditureType: value, subExpType: null },
        value,
        null,
      );
    } else if (depth === 2) {
      // depth-2 values are compound "parent|sub" strings.
      handleSubExpTypeChange(value);
    }
  }, [setFiltersWithDrillDown, handleSubExpTypeChange]);

  // Chapters the Kernhaushalt scope excludes — exactly the Sondervermögen
  // picker's options. Explicitly picking one contradicts that scope (the two
  // together select nothing), so the pick drops it; an Einzelplan pick does
  // not, since "EP 60 without its annexes" is a perfectly good view.
  const annexChapters = useMemo(
    () => new Set((filterOptions?.sondervermoegen ?? []).map(o => o.value)),
    [filterOptions],
  );

  const handleHaushaltsstelleTreeChange = useCallback((value: string | null, depth: number | null) => {
    if (!value) {
      setFiltersWithDrillDown(
        { einzelplan: null, kapitel: null },
        state.drillDownType,
        state.drillDownSubType,
      );
      return;
    }
    if (depth === 1) {
      setFiltersWithDrillDown(
        { einzelplan: value, kapitel: null },
        state.drillDownType,
        state.drillDownSubType,
      );
    } else if (depth === 2) {
      // kapitel value already encodes einzelplan ("ep|kap"); clear the
      // einzelplan slot so we don't apply both filters redundantly.
      setFiltersWithDrillDown(
        {
          einzelplan: null,
          kapitel: value,
          ...(annexChapters.has(value) ? { kapitelScope: null } : {}),
        },
        state.drillDownType,
        state.drillDownSubType,
      );
    }
  }, [setFiltersWithDrillDown, state.drillDownType, state.drillDownSubType, annexChapters]);

  const handleGruppierungTreeChange = useCallback((value: string | null, depth: number | null) => {
    const cleared = { hauptgruppe: null, obergruppe: null, gruppierung: null };
    if (!value) {
      setFiltersWithDrillDown(cleared, state.drillDownType, state.drillDownSubType);
      return;
    }
    const next =
      depth === 1 ? { ...cleared, hauptgruppe: value } :
      depth === 2 ? { ...cleared, obergruppe: value } :
      depth === 3 ? { ...cleared, gruppierung: value } :
      cleared;
    setFiltersWithDrillDown(next, state.drillDownType, state.drillDownSubType);
  }, [setFiltersWithDrillDown, state.drillDownType, state.drillDownSubType]);

  const handleFunktionTreeChange = useCallback((value: string | null, depth: number | null) => {
    const cleared = { hauptfunktion: null, oberfunktion: null, funktion: null };
    if (!value) {
      setFiltersWithDrillDown(cleared, state.drillDownType, state.drillDownSubType);
      return;
    }
    const next =
      depth === 1 ? { ...cleared, hauptfunktion: value } :
      depth === 2 ? { ...cleared, oberfunktion: value } :
      depth === 3 ? { ...cleared, funktion: value } :
      cleared;
    setFiltersWithDrillDown(next, state.drillDownType, state.drillDownSubType);
  }, [setFiltersWithDrillDown, state.drillDownType, state.drillDownSubType]);

  // Unit selector options. Hide units whose backing data couldn't be loaded;
  // parent auto-snaps displayUnit to 'absolute' when the active unit becomes
  // unavailable, so this is purely about not showing dead options.
  const unitOptions = useMemo(() => unitOptionsBase().filter(opt => {
    if (opt.value === 'gdp_percent' && gdpError) return false;
    if (opt.value === 'budget_percent' && budgetTotalsError) return false;
    return true;
  }), [gdpError, budgetTotalsError]);

  const handleClearExpTypeFilter = useCallback(() => {
    setFilter('expenditureType', null);
  }, [setFilter]);

  const handleTitleClick = useCallback((entry: BudgetTableEntry) => {
    // Toggle: if same title is clicked, deselect it
    if (state.selectedTitle?.id === entry.id) {
      selectTitle(null);
    } else {
      // Drill down to sub-type level (NOT filtering by query - show all titles in sub-type)
      // Also set einzelplan and kapitel filters if available
      if (entry.expenditure_type) {
        // Use sub_expenditure_type if available, otherwise empty string (for Einnahmen which have no sub-types)
        const subExpType = entry.sub_expenditure_type || '';
        setFiltersWithDrillDown(
          {
            // NO query filter - show all titles in sub-type
            // NO einzelplan/kapitel filter - show ALL titles in the sub-type, not just those with matching einzelplan
            expenditureType: entry.expenditure_type,
            subExpType: `${entry.expenditure_type}|${subExpType}`,
            einzelplan: null,
            kapitel: null,
          },
          entry.expenditure_type,
          subExpType
        );
      }
      // Select the title for highlighting + breadcrumb display
      selectTitle({
        id: entry.id,
        zweckbestimmung: entry.zweckbestimmung,
        titelFunktion: entry.titel_funktion,
        prefix: entry.prefix,
      });

      // Scroll chart into view (centered)
      if (areaChartRef.current) {
        areaChartRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [state.selectedTitle, selectTitle, setFiltersWithDrillDown]);

  const handleFilterByTitle = useCallback((id: string, zweckbestimmung: string, titelFunktion?: string, prefix?: string) => {
    selectTitle({ id, zweckbestimmung, titelFunktion, prefix });
  }, [selectTitle]);

  // Navigation handlers for breadcrumb
  // A cross-parent spotlight sub-type (e.g. "Bildung", plain — no "parent|sub")
  // must survive breadcrumb navigation so the spotlight scope isn't lost. For a
  // normal compound subExpType this is null, preserving the prior behavior.
  const spotlightSubExpType =
    state.filters.subExpType && !state.filters.subExpType.includes('|')
      ? state.filters.subExpType
      : null;

  const handleNavigateToRoot = useCallback(() => {
    // Go back to level 1 (root) - clear drill-down state, but keep a
    // cross-parent spotlight sub-type so "root" returns to the spotlight view.
    setFiltersWithDrillDown(
      { query: null, expenditureType: null, subExpType: spotlightSubExpType },
      null,
      null
    );
  }, [setFiltersWithDrillDown, spotlightSubExpType]);

  const handleNavigateToLevel2 = useCallback(() => {
    // Go back to expenditure type level (level 2)
    setFiltersWithDrillDown(
      { query: null, subExpType: spotlightSubExpType },
      state.drillDownType,
      null // Clear sub-type to go to level 2
    );
  }, [setFiltersWithDrillDown, state.drillDownType, spotlightSubExpType]);

  const handleDeselectTitle = useCallback(() => {
    // Deselect title but stay at current drill-down level (level 3)
    setFiltersWithDrillDown(
      { query: null },
      state.drillDownType,
      state.drillDownSubType
    );
  }, [setFiltersWithDrillDown, state.drillDownType, state.drillDownSubType]);

  // Wrapped drillUp that tracks when we're going backwards from level 3
  // This prevents auto-skip from pushing us back to level 3 when we want to go to level 1
  const handleDrillUp = useCallback(() => {
    // Only set the ref when actually transitioning level 3 → level 2
    // (not when going level 4 → 3, where selectedTitle is set).
    // Compare against null specifically: '' is a valid level-3 state for
    // auto-skipped categories (single sub-type). Without this, drilling up
    // from such a category fails to flag the move, so the auto-skip effect
    // immediately re-skips back down to level 3 instead of continuing up.
    if (!state.selectedTitle && state.drillDownSubType !== null) {
      justDrilledUpRef.current = true;
    }
    drillUp();
  }, [state.selectedTitle, state.drillDownSubType, drillUp]);

  // Wrapped drillDown that clears the justDrilledUpRef to prevent stale ref
  // from leaking into a new drill-down navigation
  const handleDrillDown = useCallback((expType: string) => {
    justDrilledUpRef.current = false;
    drillDown(expType);
  }, [drillDown]);

  // ─── Browser-history-backed drill navigation ────────────────────────────
  // The drill-down lived entirely in widget state and never touched
  // window.history, so the browser back button left the host page instead of
  // stepping back up the chart. We mirror the drill *depth* into history: each
  // step deeper pushes one entry; a popstate (back press) consumes one entry
  // and drills up one level. At depth 0 we don't intercept, so the back button
  // navigates the host page as before.
  //
  // The widget mounts directly into the host DOM (no iframe), so it shares
  // window.history. To stay a good citizen we tag our own entries with
  // `budgetDrill` and only ever react to those, and we keep history in sync in
  // both directions (UI drill-up rewinds history) so we never strand phantom
  // entries on the host's stack.
  const currentDepthRef = useRef(0);
  const historyDepthRef = useRef<number | null>(null);
  const isPopstateRef = useRef(false);
  const drillUpRef = useRef(handleDrillUp);
  drillUpRef.current = handleDrillUp;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPopState = (e: PopStateEvent) => {
      const state = e.state as { budgetDrill?: number } | null;
      const target = state && typeof state.budgetDrill === 'number' ? state.budgetDrill : 0;
      const steps = currentDepthRef.current - target;
      // Only act when landing below our current depth (a back press). Forward
      // navigation can't restore deeper state (opaque, non-URL history), and a
      // self-triggered popstate from history.go below resolves to steps === 0.
      if (steps > 0) {
        isPopstateRef.current = true;
        for (let i = 0; i < steps; i++) drillUpRef.current();
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const depth = computeDrillDepth(state.drillDownType, state.drillDownSubType, state.selectedTitle);
    currentDepthRef.current = depth;

    // First run: adopt the initial depth without pushing — the host page owns
    // the current entry and any initial drill entries don't exist yet.
    if (historyDepthRef.current === null) {
      historyDepthRef.current = depth;
      return;
    }
    if (depth === historyDepthRef.current) return;

    // Change driven by the back button: the browser already moved the history
    // position, so just resync our tracker and don't touch history again.
    if (isPopstateRef.current) {
      isPopstateRef.current = false;
      historyDepthRef.current = depth;
      return;
    }

    if (depth > historyDepthRef.current) {
      // Drilled deeper (incl. multi-level jumps from search) — one entry per level.
      for (let d = historyDepthRef.current + 1; d <= depth; d++) {
        window.history.pushState({ budgetDrill: d }, '');
      }
    } else {
      // UI-driven drill up (breadcrumb, clear-all, drill-up button): rewind
      // history to match so the back stack doesn't keep stale forward entries.
      window.history.go(-(historyDepthRef.current - depth));
    }
    historyDepthRef.current = depth;
  }, [state.drillDownType, state.drillDownSubType, state.selectedTitle]);

  // Spotlight handlers

  // True when the current filter value equals a spotlight's filter value.
  // For comma-separated values (e.g. hauptgruppe '7,8'), compare as sets so the
  // chip lights up regardless of value order.
  const filterMatchesSpotlight = (current: string | null, target: string): boolean => {
    if (current === null) return false;
    if (current === target) return true;
    if (target.includes(',')) {
      const wanted = new Set(target.split(','));
      const have = new Set(current.split(','));
      if (wanted.size !== have.size) return false;
      for (const v of wanted) if (!have.has(v)) return false;
      return true;
    }
    return false;
  };

  const activeSpotlightId = useMemo(() => {
    // For a spotlight to be "active", its filter must match AND no other
    // narrowing filters may be present — otherwise the chip would lie about
    // what the user is actually looking at.
    const otherFiltersActive =
      !!state.filters.einzelplan ||
      !!state.filters.funktion ||
      !!state.filters.oberfunktion ||
      !!state.filters.hauptfunktion ||
      !!state.filters.gruppierung ||
      !!state.filters.obergruppe ||
      !!state.filters.query;
    if (otherFiltersActive) return null;

    for (const spotlight of SPOTLIGHTS) {
      const filterKey = spotlight.filterType === 'kapitel' ? 'kapitel' :
                        spotlight.filterType === 'subExpType' ? 'subExpType' :
                        spotlight.filterType === 'prefix' ? 'prefix' :
                        spotlight.filterType === 'hauptgruppe' ? 'hauptgruppe' :
                        spotlight.filterType === 'kapitelScope' ? 'kapitelScope' : 'expenditureType';
      if (filterMatchesSpotlight(state.filters[filterKey], spotlight.filterValue)) {
        return spotlight.id;
      }
    }
    return null;
    // The reducer creates a new filters object on every change, so depending on
    // the whole object catches any field — including ones SPOTLIGHTS may use
    // in the future. Listing individual keys was forward-incompatible.
  }, [state.filters]);

  const handleSpotlightClick = useCallback((spotlight: Spotlight) => {
    // Spotlights are top-level "view presets". Reset all filters so the
    // spotlight's scope is unambiguous — no leftover einzelplan/funktion/
    // gruppierung silently narrows what the chip claims to show.
    clearFilters();

    // Set the new spotlight's filter on top of the clean state.
    switch (spotlight.filterType) {
      case 'kapitel':
        handleKapitelChange(spotlight.filterValue);
        break;
      case 'subExpType':
        handleSubExpTypeChange(spotlight.filterValue);
        break;
      case 'expenditureType':
        handleExpenditureTypeChange(spotlight.filterValue);
        break;
      case 'prefix':
        handlePrefixChange(spotlight.filterValue);
        break;
      case 'hauptgruppe':
        handleHauptgruppeChange(spotlight.filterValue);
        break;
      case 'kapitelScope':
        handleKapitelScopeChange(spotlight.filterValue);
        break;
    }
  }, [clearFilters, handleKapitelChange, handleSubExpTypeChange, handleExpenditureTypeChange, handlePrefixChange, handleHauptgruppeChange, handleKapitelScopeChange]);

  const handleClearSpotlight = useCallback(() => {
    const active = SPOTLIGHTS.find(s => s.id === activeSpotlightId);
    if (active) {
      switch (active.filterType) {
        case 'kapitel': handleKapitelChange(null); break;
        case 'subExpType':
          // subExpType spotlights set both expenditureType and subExpType, so clear both
          handleSubExpTypeChange(null);
          handleExpenditureTypeChange(null);
          break;
        case 'expenditureType': handleExpenditureTypeChange(null); break;
        case 'prefix': handlePrefixChange(null); break;
        case 'hauptgruppe': handleHauptgruppeChange(null); break;
        case 'kapitelScope': handleKapitelScopeChange(null); break;
      }
    }
  }, [activeSpotlightId, handleKapitelChange, handleSubExpTypeChange, handleExpenditureTypeChange, handlePrefixChange, handleHauptgruppeChange, handleKapitelScopeChange]);

  // Per-chip removal for the ActiveFilterChips bar. Each case mirrors the
  // control that set the slot, so removing a chip leaves exactly the state
  // the user would get by clearing that control: the tree handlers own their
  // dimension's cascade (only one slot per tree is ever set), while
  // einzelplan/kapitel and the search term clear in isolation.
  const handleRemoveFilter = useCallback((key: ChipKey) => {
    switch (key) {
      case 'query':
        // SearchAutocomplete mirrors filters.query, so its input clears too.
        setFilter('query', null);
        break;
      case 'expenditureType':
        // Clears subExpType with it — a held sub-type belongs to this parent.
        handlePolitikfeldTreeChange(null, null);
        break;
      case 'subExpType':
        // Keep the Oberkategorie, drop the sub-type and its drill level.
        setFiltersWithDrillDown(
          { subExpType: null },
          state.filters.expenditureType ?? state.drillDownType,
          null,
        );
        break;
      case 'einzelplan':
      case 'kapitel':
        setFiltersWithDrillDown({ [key]: null }, state.drillDownType, state.drillDownSubType);
        break;
      case 'hauptgruppe':
      case 'obergruppe':
      case 'gruppierung':
        handleGruppierungTreeChange(null, null);
        break;
      case 'hauptfunktion':
      case 'oberfunktion':
      case 'funktion':
        handleFunktionTreeChange(null, null);
        break;
      case 'prefix':
        handlePrefixChange(null);
        break;
      case 'kapitelScope':
        handleKapitelScopeChange(null);
        break;
      // Drill chips remove by navigating the drill up to the level above,
      // which is exactly what the chart breadcrumb's own links do.
      case 'drillDownType':
        handleNavigateToRoot();
        break;
      case 'drillDownSubType':
        handleNavigateToLevel2();
        break;
      case 'selectedTitle':
        handleDeselectTitle();
        break;
    }
  }, [
    setFilter,
    setFiltersWithDrillDown,
    handlePolitikfeldTreeChange,
    handleGruppierungTreeChange,
    handleFunktionTreeChange,
    handlePrefixChange,
    handleKapitelScopeChange,
    handleNavigateToRoot,
    handleNavigateToLevel2,
    handleDeselectTitle,
    state.filters.expenditureType,
    state.drillDownType,
    state.drillDownSubType,
  ]);

  const hasActiveFilters = !!(
    state.filters.einzelplan ||
    state.filters.kapitel ||
    state.filters.query ||
    state.filters.expenditureType ||
    state.filters.subExpType ||
    state.filters.funktion ||
    state.filters.oberfunktion ||
    state.filters.hauptfunktion ||
    state.filters.gruppierung ||
    state.filters.obergruppe ||
    state.filters.hauptgruppe ||
    state.filters.prefix ||
    state.filters.kapitelScope
  );

  // Block initial render when data the first paint depends on is still in flight.
  // budgetTotals gates every unit: yearsWithIst derives from it, and without it the
  // chart renders ALL years as Soll — inflated areas that visibly shrink when the
  // totals land. gdpData additionally gates '% des BIP' so absolute thousands are
  // never rendered as if they were percentages. Each term clears on fetch failure
  // (no infinite spinner): errors trigger the auto-snap to 'absolute' above, and a
  // totals-less render is acceptable degraded UI (every year Soll-shaded).
  const isUnitDataLoading =
    (budgetTotals === null && !budgetTotalsError) ||
    (state.displayUnit === 'gdp_percent' && gdpData === null && !gdpError);

  // Show loading state while fetching initial chart data only.
  if ((isLoading && !data) || isUnitDataLoading) {
    return (
      <WidgetContainer isDark={themeMode === 'dark'} className={`dz-budget-widget-root color-data ${className ?? ''}`.trim()}>
        <InitialLoadingState>
          <Spinner />
          <LoadingText>{m.widget.loading}</LoadingText>
        </InitialLoadingState>
      </WidgetContainer>
    );
  }

  // Build error banner content
  const errorList: string[] = [];
  if (gdpError) errorList.push(m.widget.errorPartGdp);
  if (budgetTotalsError) errorList.push(m.widget.errorPartTotals);
  if (filterOptionsError) errorList.push(m.widget.errorPartFilterOptions);
  const showBanner = errorList.length > 0 && !bannerDismissed;

  // Show error state
  if (error && !data) {
    return (
      <WidgetContainer isDark={themeMode === 'dark'} className={`dz-budget-widget-root color-data ${className ?? ''}`.trim()}>
        <ErrorMessage>
          {m.widget.loadError(error)}
        </ErrorMessage>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer isDark={themeMode === 'dark'} className={`dz-budget-widget-root color-data ${className ?? ''}`.trim()}>
      {showBanner && (
        <ErrorBanner role="alert">
          <span>
            {m.widget.errorBanner(errorList.join(', '))}
          </span>
          <ErrorBannerActions>
            <ErrorBannerRetry onClick={handleRetry}>{m.widget.retry}</ErrorBannerRetry>
            <ErrorBannerClose onClick={() => setBannerDismissed(true)} aria-label={m.widget.dismissNotice}>
              <span className="icon" data-icon="close" />
            </ErrorBannerClose>
          </ErrorBannerActions>
        </ErrorBanner>
      )}
      {toast && <ToastContainer role="status">{toast}</ToastContainer>}
      <Spotlights
        activeSpotlightId={activeSpotlightId}
        onSpotlightClick={handleSpotlightClick}
        onClearSpotlight={handleClearSpotlight}
      />

      {/* Main Content: top controls (type + unit) above a two-column body
          (left: search + charts, right: persistent filter sidebar). */}
      <MainContentArea>
        <TopControlsRow>
          <TypeToggleWrapper role="group" aria-label={m.widget.typeToggleLabel}>
            <TypeToggleButton
              isActive={state.filters.type === 'E'}
              onClick={() => state.filters.type !== 'E' && setType('E')}
            >
              {m.widget.revenue}
            </TypeToggleButton>
            <TypeToggleButton
              isActive={state.filters.type === 'A'}
              onClick={() => state.filters.type !== 'A' && setType('A')}
            >
              {m.widget.expenditure}
            </TypeToggleButton>
          </TypeToggleWrapper>
          <UnitDropdownLabeled data-label={m.widget.unit}>
            <UnitNativeSelect
              value={state.displayUnit}
              onChange={(e) => setDisplayUnit(e.target.value as DisplayUnit)}
              aria-label={m.widget.unit}
            >
              {unitOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </UnitNativeSelect>
          </UnitDropdownLabeled>
        </TopControlsRow>

        <ContentSplit>
          <MainColumn>
            <SearchBlock>
              <SearchAutocomplete
                initialValue={state.filters.query || ''}
                apiClient={apiClient}
                onSelect={handleSearchSelect}
                budgetType={state.filters.type}
              />
              <ActiveFilterChips
                filters={state.filters}
                filterOptions={filterOptions}
                drillDownType={state.drillDownType}
                drillDownSubType={state.drillDownSubType}
                selectedTitle={state.selectedTitle}
                onRemove={handleRemoveFilter}
                onClearAll={clearFilters}
              />
            </SearchBlock>

            <ChartsSection>
              {data && (
                <>
                  <ChartSection>
                    <ExpenditureAreaChart
                  ref={areaChartRef}
                  data={data.expenditureByYearAndType}
                  subData={subData}
                  titleData={titleData}
                  subColorMap={subColorMap}
                  titleColorMap={titleColorMap}
                  isLoading={isLoading}
                  isLoadingSubData={isLoadingSubData}
                  isLoadingTitleData={isLoadingTitleData}
                  drillDownType={state.drillDownType}
                  drillDownSubType={state.drillDownSubType}
                  selectedTitle={state.selectedTitle}
                  brokenOutTitleIds={state.brokenOutTitleIds}
                  onToggleTitleBreakout={toggleTitleBreakout}
                  filters={state.filters}
                  onDrillDown={handleDrillDown}
                  onDrillDownSub={drillDownSub}
                  onDrillUp={handleDrillUp}
                  onFilterByTitle={handleFilterByTitle}
                  onNavigateToRoot={handleNavigateToRoot}
                  onNavigateToLevel2={handleNavigateToLevel2}
                  onDeselectTitle={handleDeselectTitle}
                  activeFilter={state.filters.expenditureType}
                  onClearFilter={handleClearExpTypeFilter}
                  onEmbed={() => setEmbedChart('area')}
                  budgetType={state.filters.type}
                  displayUnit={state.displayUnit}
                  gdpData={gdpData}
                  budgetTotals={budgetTotals}
                  yearsWithIst={yearsWithIst}
                  activeFilters={{
                    einzelplan: state.filters.einzelplan,
                    kapitel: state.filters.kapitel,
                    funktion: state.filters.funktion,
                    oberfunktion: state.filters.oberfunktion,
                    hauptfunktion: state.filters.hauptfunktion,
                    gruppierung: state.filters.gruppierung,
                    obergruppe: state.filters.obergruppe,
                    hauptgruppe: state.filters.hauptgruppe,
                    prefix: state.filters.prefix,
                    kapitelScope: state.filters.kapitelScope,
                    query: state.filters.query,
                  }}
                  filterOptions={filterOptions ? {
                    einzelplan: filterOptions.einzelplan,
                    kapitel: filterOptions.kapitel,
                    funktion: filterOptions.funktion,
                    oberfunktion: filterOptions.oberfunktion,
                    hauptfunktion: filterOptions.hauptfunktion,
                    gruppierung: filterOptions.gruppierung,
                    obergruppe: filterOptions.obergruppe,
                    hauptgruppe: filterOptions.hauptgruppe,
                  } : null}
                  apiClient={apiClient}
                />
                  </ChartSection>
                  <ChartSection>
                    <ExpenditureBarChart
                  data={data.expenditureByYearAndType}
                  subData={subData}
                  titleData={titleData}
                  subColorMap={subColorMap}
                  titleColorMap={titleColorMap}
                  isLoading={isLoading}
                  isLoadingSubData={isLoadingSubData}
                  isLoadingTitleData={isLoadingTitleData}
                  selectedYear={effectiveYear}
                  onYearChange={setYear}
                  drillDownType={state.drillDownType}
                  drillDownSubType={state.drillDownSubType}
                  selectedTitle={state.selectedTitle}
                  onDrillDown={handleDrillDown}
                  onDrillDownSub={drillDownSub}
                  onDrillUp={handleDrillUp}
                  onFilterByTitle={handleFilterByTitle}
                  onNavigateToRoot={handleNavigateToRoot}
                  onNavigateToLevel2={handleNavigateToLevel2}
                  onDeselectTitle={handleDeselectTitle}
                  activeType={state.filters.expenditureType}
                  onClearFilter={handleClearExpTypeFilter}
                  onEmbed={() => setEmbedChart('bar')}
                  budgetType={state.filters.type}
                  displayUnit={state.displayUnit}
                  gdpData={gdpData}
                  budgetTotals={budgetTotals}
                  yearsWithIst={yearsWithIst}
                  activeFilters={{
                    einzelplan: state.filters.einzelplan,
                    kapitel: state.filters.kapitel,
                    funktion: state.filters.funktion,
                    oberfunktion: state.filters.oberfunktion,
                    hauptfunktion: state.filters.hauptfunktion,
                    gruppierung: state.filters.gruppierung,
                    obergruppe: state.filters.obergruppe,
                    hauptgruppe: state.filters.hauptgruppe,
                    prefix: state.filters.prefix,
                    kapitelScope: state.filters.kapitelScope,
                    query: state.filters.query,
                  }}
                  filterOptions={filterOptions ? {
                    einzelplan: filterOptions.einzelplan,
                    kapitel: filterOptions.kapitel,
                    funktion: filterOptions.funktion,
                    oberfunktion: filterOptions.oberfunktion,
                    hauptfunktion: filterOptions.hauptfunktion,
                    gruppierung: filterOptions.gruppierung,
                    obergruppe: filterOptions.obergruppe,
                    hauptgruppe: filterOptions.hauptgruppe,
                  } : null}
                    />
                  </ChartSection>
                </>
              )}
            </ChartsSection>
          </MainColumn>

          <Sidebar>
            {filterOptions && (
              <FilterPanel
                filterOptions={filterOptions}
                filters={state.filters}
                onPolitikfeldChange={handlePolitikfeldTreeChange}
                onHaushaltsstelleChange={handleHaushaltsstelleTreeChange}
                onGruppierungChange={handleGruppierungTreeChange}
                onFunktionChange={handleFunktionTreeChange}
                onClearAll={clearFilters}
                hasActiveFilters={hasActiveFilters}
                availability={filterAvailability}
              />
            )}
          </Sidebar>
        </ContentSplit>
      </MainContentArea>

      {/* Budget Entries Table */}
      <BudgetTable
        entries={entries}
        totalCount={totalCount}
        isLoading={entriesLoading}
        currentPage={currentPage}
        pageCount={pageCount}
        onPageChange={goToPage}
        selectedTitleId={state.selectedTitle?.id ?? null}
        onTitleClick={handleTitleClick}
        drillDownType={state.drillDownType}
        drillDownSubType={state.drillDownSubType}
        selectedTitle={state.selectedTitle}
        onNavigateToRoot={handleNavigateToRoot}
        onNavigateToLevel2={handleNavigateToLevel2}
        onDeselectTitle={handleDeselectTitle}
        subColorMap={subColorMap}
        titleColorMap={titleColorMap}
        selectedYear={effectiveYear}
        yearsWithIst={yearsWithIst}
        apiClient={apiClient}
        budgetType={state.filters.type}
        filters={state.filters}
      />
      <FooterRemark>
        <FooterDates>
          {m.widget.footerDates(lastUpdatedLabel)}
        </FooterDates>
        <FooterText>
          {m.widget.methodology}
        </FooterText>
        <FooterContact>
          {m.widget.contact}{' '}
          <a href="mailto:alexander.marx@dezernatzukunft.org">alexander.marx@dezernatzukunft.org</a>
        </FooterContact>
      </FooterRemark>

      {embedChart && (
        <EmbedDialog
          chart={embedChart}
          chartTitle={embedChartTitle}
          view={{
            filters: state.filters,
            // The bar chart is a single-year view, so it always offers a
            // concrete year to pin. The area chart spans the whole period, so
            // it embeds without one and follows each new Haushalt.
            year: embedChart === 'bar' ? effectiveYear : null,
            displayUnit: state.displayUnit,
            drillDownType: state.drillDownType,
            drillDownSubType: state.drillDownSubType,
            titleId: state.selectedTitle?.id ?? null,
            brokenOutTitleIds: state.brokenOutTitleIds,
          }}
          onClose={() => setEmbedChart(null)}
        />
      )}
    </WidgetContainer>
  );
}
